import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { LinkItem } from '@frame/ui-components/unified-search/link-item'
import { WidgetHeaderWithNavigation } from '@frame/ui-components/widget-header'
import { Body } from '@frame/ui-primitives/body'
import { H3 } from '@frame/ui-primitives/h3'
import { Input } from '@frame/ui-primitives/input'

import { buildLogoDevUrl } from '@frame/lib/constants'
import { useFuzzySearch } from '@frame/lib/hooks/useFuzzySearch'
import { ItemKind, ItemMappingStatus, ItemStatus } from '@frame/lib/hooks/useGetItems'
import { SandboxState, useSandboxStore } from '@frame/lib/stores/useSandboxStore'
import { useStore } from '@frame/lib/stores/useStore'
import { routes } from '@frame/routes'
import { DataAttribute } from '@frame/types'

const popularEmployers = [
  { name: 'Spotify', domain: 'www.spotify.com', logo: '' },
  { name: 'Target', domain: 'www.target.com', logo: '' },
  { name: 'Lyft', domain: 'www.lyft.com', logo: '' },
  { name: 'Uber', domain: 'www.uber.com', logo: '' },
  { name: 'Chipotle', domain: 'www.chipotle.com', logo: '' },
  { name: 'McDonalds', domain: 'www.mcdonalds.com', logo: '' },
  { name: 'Subway', domain: 'www.subway.com', logo: '' },
]

const employers = [
  {
    name: "McDonald's",
    domain: 'mcdonalds.com',
  },
  {
    name: 'Amazon',
    domain: 'amazon.com',
  },
  {
    name: 'Apple',
    domain: 'apple.com',
  },
  {
    name: 'Google',
    domain: 'google.com',
  },
  {
    name: 'Microsoft',
    domain: 'microsoft.com',
  },
  {
    name: 'AT&T',
    domain: 'att.com',
  },
  {
    name: 'Verizon',
    domain: 'verizon.com',
  },
  {
    name: 'Subway',
    domain: 'subway.com',
  },
  {
    name: 'Walgreens',
    domain: 'walgreens.com',
  },
  {
    name: 'Chase',
    domain: 'chase.com',
  },
  {
    name: 'Wells Fargo',
    domain: 'wellsfargo.com',
  },
  {
    name: 'Citibank',
    domain: 'citibank.com',
  },
  {
    name: 'JPMorgan Chase',
    domain: 'jpmorganchase.com',
  },
  {
    name: 'Goldman Sachs',
    domain: 'goldmansachs.com',
  },
  {
    name: 'Morgan Stanley',
    domain: 'morganstanley.com',
  },
  {
    name: 'Berkshire Hathaway',
    domain: 'berkshirehathaway.com',
  },
  {
    name: 'Visa',
    domain: 'visa.com',
  },
  {
    name: 'Mastercard',
    domain: 'mastercard.com',
  },
  {
    name: 'American Express',
    domain: 'americanexpress.com',
  },
  {
    name: 'PepsiCo',
    domain: 'pepsico.com',
  },
  {
    name: 'Coca-Cola',
    domain: 'coca-cola.com',
  },
  {
    name: 'Nestlé',
    domain: 'nestle.com',
  },
  {
    name: 'Kraft Heinz',
    domain: 'kraftheinzcompany.com',
  },
  {
    name: 'General Mills',
    domain: 'generalmills.com',
  },
  {
    name: 'Procter & Gamble',
    domain: 'pg.com',
  },
  {
    name: 'Unilever',
    domain: 'unilever.com',
  },
  {
    name: 'Johnson & Johnson',
    domain: 'jnj.com',
  },
  {
    name: 'Pfizer',
    domain: 'pfizer.com',
  },
  {
    name: 'Merck',
    domain: 'merck.com',
  },
  {
    name: 'AbbVie',
    domain: 'abbvie.com',
  },
  {
    name: 'Gilead Sciences',
    domain: 'gilead.com',
  },
  {
    name: 'Amgen',
    domain: 'amgen.com',
  },
  {
    name: 'Walmart',
    domain: 'walmart.com',
  },
]

const options = {
  keys: ['name', 'domain'],
}

export function Search() {
  const navigate = useNavigate()
  const consumerInitiated = useStore((state) => state.session?.consumer_initiated)
  const { setCompany, setState } = useSandboxStore(({ setState, setCompany }) => ({
    setCompany,
    setState,
  }))
  const [query, setQuery] = useState('')

  const fuzzySearch = useFuzzySearch(employers, options)
  const filteredEmployers = useMemo(() => {
    return query === '' ? employers : fuzzySearch(query)
  }, [fuzzySearch, query])

  if (filteredEmployers.length <= 3) {
    filteredEmployers.push({ name: query, domain: '' })
  }

  function goBack() {
    navigate(-1)
  }

  return (
    <>
      <WidgetHeaderWithNavigation
        backButton={{ visible: consumerInitiated, onClick: goBack }}
        cancelButton={{ onClick: () => navigate(routes.confirmExit), visible: true }}
      />
      <Body data-cy={DataAttribute.PARTNER_SCREEN_SANDBOX_SEARCH} className="p-5" paddingTopOverride={0}>
        <H3 className="font-medium">Who pays you?</H3>
        <Input
          tagType={null}
          label="Employer Name"
          placeholder="Search for your employer"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="mt-4"
        />
        <H3 className="mb-2 mt-5 font-medium">Popular Employers</H3>
        <div
          className="scrollbar-hide flex w-full flex-shrink-0 items-center justify-start space-x-4 overflow-x-auto scroll-smooth p-1"
          data-cy={DataAttribute.PARTNER_SCREEN_SANDBOX_SEARCH_POPULAR_COMPANIES}
        >
          {popularEmployers.map((emp) => (
            <button
              key={emp.name}
              className="grid h-[3rem] w-[7rem] flex-shrink-0 place-items-center rounded-md border border-solid border-outline hover:ring-2 hover:ring-electric-650 hover:ring-offset-2 focus:outline-none focus:ring-2 focus:ring-electric-650 focus:ring-offset-2"
              onClick={() => {
                setCompany(emp)
                setState(SandboxState.FORM)
              }}
            >
              <img
                src={buildLogoDevUrl(emp.domain, 32)}
                className="aspect-video w-[5rem] object-contain object-center p-2"
              />
            </button>
          ))}
        </div>
        <H3 className="mb-2 mt-5 font-medium">All Employers</H3>
        <div className="flex flex-col">
          {filteredEmployers.map((emp) => (
            <LinkItem
              key={emp.name}
              item={{
                id: '',
                name: emp.name,
                kind: ItemKind.EMPLOYER,
                known_limitations: null,
                status: ItemStatus.HEALTHY,
                status_details: '',
                mapping_status: ItemMappingStatus.MAPPED,
                logo_url: emp.domain !== '' ? buildLogoDevUrl(emp.domain) : '',
                is_grouping: false,
              }}
              handleSelect={() => {
                setCompany({ ...emp, logo: '' })
                setState(SandboxState.FORM)
              }}
              className="py-3"
            />
          ))}
        </div>
      </Body>
    </>
  )
}
